import { z } from 'zod'
import { locationCreateSchema } from '../validation/location'
import { nonEmptyString } from '../validation/strings'
import { taxonomyTagsSchema } from '../validation/taxonomy'
import { timeOfDay } from '../validation/time'
import { dropTeaserDtoSchema } from './drop'
import { placeGoodForOccasionSchema } from './good-for'
import { imageSchema } from './image'
import { activeListingActionSchema } from './listing-action'
import { listingButtonKindSchema } from './listing-buttons'
import { locationSchema } from './location'
import { windowedQuerySchema, windowedResponseSchema } from './pagination'
import { groupedTagsSchema } from './tag'

export const openingHourSchema = z.object({
  day: z.string(),
  open: z.string(),
  close: z.string(),
})

export const listingDtoSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  description: z.string(),
  rating: z.number(),
  reviewCount: z.number(),
  interestedCount: z.number(),
  location: locationSchema,
  postalCode: z.string().nullable(),
  /** 1 to 4, shown as € to €€€€; null when the place has not said. */
  priceLevel: z.number().int().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  facebook: z.string().nullable(),
  instagram: z.string().nullable(),
  twitter: z.string().nullable(),
  /** What guests call the place locally, set by its owner; venue search matches these too. */
  nicknames: z.array(z.string()),
  tags: groupedTagsSchema,
  images: z.array(imageSchema),
  coverImageUrl: z.string().nullable(),
  hours: z.array(openingHourSchema),
  status: z.enum(['importing', 'draft', 'published', 'unlisted']),
  isDraft: z.boolean(),
  /** Live drop linked to this listing; only set on the public detail response. */
  liveDrop: dropTeaserDtoSchema.nullable().optional(),
  /** The place page's buttons in display order; only set on the public detail response. */
  buttons: z.array(listingButtonKindSchema).optional(),
  /** Which menus the place page can open; only set on the public detail response. */
  menus: z.object({ lunch: z.boolean(), full: z.boolean() }).optional(),
  /** The place's running action; only set on the public detail response. */
  activeAction: activeListingActionSchema.nullable().optional(),
  /** Search-index facts; only set on the public detail response. */
  seo: z.object({ indexable: z.boolean() }).optional(),
  /** Published "good for" labels; only set on the public detail response. */
  goodFor: z.array(placeGoodForOccasionSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type OpeningHour = z.infer<typeof openingHourSchema>
export type ListingDto = z.infer<typeof listingDtoSchema>

/** A place's price level: 1 to 4, shown as € to €€€€. */
export const listingPriceLevelSchema = z.number().int().min(1).max(4)

export const timeRangeSchema = z.strictObject({
  open: timeOfDay,
  close: timeOfDay,
})

const DAY_KEYS = ['0', '1', '2', '3', '4', '5', '6'] as const

export const openingHoursMapSchema = z.partialRecord(z.enum(DAY_KEYS), z.array(timeRangeSchema))

export const createListingRequestSchema = z.strictObject({
  name: nonEmptyString,
  description: z.string(),
  location: locationCreateSchema,
  postalCode: z.string().trim().max(12).nullable().optional(),
  priceLevel: listingPriceLevelSchema.nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  twitter: z.string().nullable().optional(),
  nicknames: z.array(z.string().trim().min(1).max(40)).max(5).optional(),
  hours: openingHoursMapSchema,
  tags: taxonomyTagsSchema,
})

export const updateListingRequestSchema = createListingRequestSchema.partial()

export const listingCreatedResponseSchema = z.object({
  id: z.string().uuid(),
})

export const listingListResponseSchema = windowedResponseSchema(listingDtoSchema)

export const listingListQuerySchema = windowedQuerySchema
export type ListingListQuery = z.infer<typeof listingListQuerySchema>

export const listingStatusActionRequestSchema = z.strictObject({
  action: z.enum(['publish']),
})

export type CreateListingRequest = z.infer<typeof createListingRequestSchema>
export type UpdateListingRequest = z.infer<typeof updateListingRequestSchema>
export type ListingCreatedResponse = z.infer<typeof listingCreatedResponseSchema>
export type ListingListResponse = z.infer<typeof listingListResponseSchema>
